import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import Modal from './Modal';
import miaPlatformDataService from '../services/MiaPlatformDataService';
import './CreateProjectModal.css';

/**
 * Modal component for creating a new Mia Platform project from a template
 */
const CreateProjectModal = ({
  isOpen,
  onClose,
  onCreateProject,
  selectedTenant,
  initialPrompt = '',
  loading = false
}) => {
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState(null);
  const [creating, setCreating] = useState(false);
  
  // AI name suggestion states
  const [generatingName, setGeneratingName] = useState(false);
  const [suggestedName, setSuggestedName] = useState('');
  const [originalName, setOriginalName] = useState('');
  const [showNameSuggestion, setShowNameSuggestion] = useState(false);

  // Set initial description from prompt
  useEffect(() => {
    if (isOpen && initialPrompt) {
      // Ensure initialPrompt is a string
      const promptStr = typeof initialPrompt === 'string' ? initialPrompt : '';
      setProjectDescription(promptStr);
    }
  }, [isOpen, initialPrompt]);

  // Fetch templates when modal opens and tenant is selected
  useEffect(() => {
    if (isOpen && selectedTenant) {
      fetchTemplates();
    }
  }, [isOpen, selectedTenant]);

  // Auto-generate name when description is set from initialPrompt
  useEffect(() => {
    if (isOpen && projectDescription && initialPrompt) {
      handleGenerateProjectName();
    }
  }, [projectDescription, isOpen, initialPrompt]);

  const fetchTemplates = async () => {
    if (!selectedTenant || !selectedTenant.id) {
      console.warn('Cannot fetch templates: selectedTenant or selectedTenant.id is missing', selectedTenant);
      return;
    }

    setTemplatesLoading(true);
    setTemplatesError(null);

    try {
      const templates = await miaPlatformDataService.getTemplates(selectedTenant.id);
      
      // Transform templates to have consistent id/name structure
      // Use templateId (UUID) as the primary ID, not _id (MongoDB ID)
      const transformedTemplates = templates.map(template => ({
        id: template.templateId || template._id || template.id,
        templateId: template.templateId, // Keep templateId for BFF
        _id: template._id, // Keep _id for reference
        name: template.name,
        description: template.description,
        ...template
      }));
      setTemplates(transformedTemplates);
      
      // Auto-select default template if available
      const defaultTemplate = transformedTemplates.find(t => t.id === selectedTenant.defaultTemplateId);
      if (defaultTemplate) {
        setSelectedTemplate(defaultTemplate);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      setTemplatesError(error.message);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const handleGenerateProjectName = async () => {
    const descStr = String(projectDescription || '');
    if (!descStr.trim()) {
      return;
    }

    setGeneratingName(true);
    setShowNameSuggestion(false);

    try {
      const result = await miaPlatformDataService.generateProjectName(
        descStr.trim(),
        String(projectName || '').trim() || null
      );
      
      setOriginalName(projectName.trim() || '');
      setSuggestedName(result.suggestedName);
      setShowNameSuggestion(true);
      
      // Auto-apply the suggested name
      setProjectName(result.suggestedName);
    } catch (error) {
      console.error('Error generating project name:', error);
      // Don't show error to user, just silently fail
    } finally {
      setGeneratingName(false);
    }
  };

  const handleDescriptionChange = (e) => {
    setProjectDescription(e.target.value);
    // Reset name suggestion when description changes
    setShowNameSuggestion(false);
    setSuggestedName('');
    setOriginalName('');
  };

  const handleAcceptSuggestedName = () => {
    if (suggestedName) {
      setProjectName(suggestedName);
      setShowNameSuggestion(false);
    }
  };

  const handleRejectSuggestedName = () => {
    setProjectName(originalName);
    setShowNameSuggestion(false);
    setSuggestedName('');
    setOriginalName('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!projectDescription.trim()) {
      alert('Please enter a project description');
      return;
    }

    if (!projectName.trim()) {
      alert('Please enter a project name');
      return;
    }

    if (!selectedTemplate) {
      alert('Please select a template');
      return;
    }

    if (!selectedTenant || !selectedTenant.id) {
      alert('Selected tenant is invalid. Please reselect a tenant.');
      return;
    }

    setCreating(true);

    try {
      await onCreateProject({
        tenantId: selectedTenant.id,
        projectName: projectName.trim(),
        projectDescription: projectDescription.trim(),
        templateId: selectedTemplate.templateId || selectedTemplate.id
      });
      
      // Reset form
      setProjectName('');
      setProjectDescription('');
      setSelectedTemplate(null);
      setSuggestedName('');
      setOriginalName('');
      setShowNameSuggestion(false);
      onClose();
    } catch (error) {
      console.error('Error creating project:', error);
      alert(`Failed to create project: ${error.message}`);
    } finally {
      setCreating(false);
    }
  };

  const handleClose = () => {
    if (!creating) {
      setProjectName('');
      setProjectDescription('');
      setSelectedTemplate(null);
      setSuggestedName('');
      setOriginalName('');
      setShowNameSuggestion(false);
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Create New Project" type="custom" className="create-project-modal">
      {!selectedTenant || !selectedTenant.id ? (
        <div className="error-state" style={{ padding: '20px', textAlign: 'center' }}>
          <p>{'⚠️ Please select an organization first before creating a project.'}</p>
          <button
            className="btn btn-secondary"
            onClick={handleClose}
            style={{ marginTop: '20px' }}
            type="button"
          >
            {'Close'}
          </button>
        </div>
      ) : (
        <form className="create-project-form" onSubmit={handleSubmit}>
          <div className="form-scroll-body">
            <div className="form-group">
              <label htmlFor="organization">
                {'Organization'}
                <span className="required">{'*'}</span>
              </label>
              <div className="form-control-static">
                {selectedTenant.name}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="projectDescription">
                {'Project Description'}
                <span className="required">{'*'}</span>
              </label>
              <textarea
                disabled={creating}
                id="projectDescription"
                onChange={handleDescriptionChange}
                onBlur={handleGenerateProjectName}
                placeholder="Describe your project... (AI will suggest a name)"
                rows={2}
                value={projectDescription}
                required
              />
              <small className="form-hint">
                {'Describe your project and AI will suggest a name automatically'}
              </small>
            </div>

            {/* Name Suggestion Display */}
            {showNameSuggestion && suggestedName && (
              <div className="name-suggestion-panel">
                <div className="name-suggestion-header">
                  <span style={{ fontSize: '16px' }}>🤖</span>
                  <strong>AI Suggested Name</strong>
                </div>
                <div>
                  {originalName && (
                    <div className="name-suggestion-original">
                      <span style={{ textDecoration: 'line-through' }}>{originalName || '(none)'}</span>
                      {' → '}
                    </div>
                  )}
                  <div className="name-suggestion-value">
                    {suggestedName}
                  </div>
                </div>
                <div className="name-suggestion-actions">
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={handleAcceptSuggestedName}
                    type="button"
                  >
                    ✓ Use This Name
                  </button>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={handleRejectSuggestedName}
                    type="button"
                  >
                    ✗ Keep Original
                  </button>
                </div>
              </div>
            )}

            {generatingName && (
              <div className="generating-name-indicator">
                <span className="spinner-small" />
                <span>Generating project name from description...</span>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="projectName">
                {'Project Name'}
                <span className="required">{'*'}</span>
              </label>
              <input
                disabled={creating}
                id="projectName"
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="Enter project name"
                required
                type="text"
                value={projectName}
              />
              <small className="form-hint">
                {'Must be unique within the organization'}
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="template">
                {'Template'}
                <span className="required">{'*'}</span>
              </label>
              
              {templatesLoading ? (
                <div className="templates-loading">
                  <span className="spinner" />
                  {'Loading templates...'}
                </div>
              ) : templatesError ? (
                <div className="templates-error">
                  {`Error: ${templatesError}`}
                </div>
              ) : (
                <select
                  className="form-select"
                  disabled={creating || templates.length === 0}
                  id="template"
                  onChange={(e) => {
                    const template = templates.find(t => t.id === e.target.value);
                    setSelectedTemplate(template || null);
                  }}
                  required
                  value={selectedTemplate?.id || ''}
                >
                  <option value="">
                    {templates.length === 0 ? 'No templates available' : 'Select a template'}
                  </option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>
              )}
              {selectedTemplate?.description && (
                <small className="form-hint">{selectedTemplate.description}</small>
              )}
            </div>
          </div>{/* end form-scroll-body */}

          <div className="form-actions">
            <button
              className="btn btn-secondary"
              disabled={creating}
              onClick={handleClose}
              type="button"
            >
              {'Cancel'}
            </button>
            <button
              className="btn btn-primary"
              disabled={creating || !String(projectDescription || '').trim() || !String(projectName || '').trim() || !selectedTemplate}
              type="submit"
            >
              {creating ? (
                <>
                  <span className="spinner-small" />
                  {'Creating...'}
                </>
              ) : (
                'Create Project'
              )}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
};

CreateProjectModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  loading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onCreateProject: PropTypes.func.isRequired,
  selectedTenant: PropTypes.shape({
    defaultTemplateId: PropTypes.string,
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  initialPrompt: PropTypes.string,
};

export default CreateProjectModal;
